// Lab 3 safety invariants: one value per slot, contiguous execution,
// at-most-once application effects, safe garbage collection, and client
// result correspondence.
export function slotAgreement(state) {
  const chosen = {};
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    node.volatile.log.forEach((slot, i) => {
      if (!slot || slot.status !== 'chosen') return;
      const key = JSON.stringify({ command: slot.command, client: slot.client, seq: slot.seq });
      if (chosen[i] && chosen[i] !== key) {
        chosen[i] = 'CONFLICT';
      } else {
        chosen[i] = key;
      }
    });
  }
  const bad = Object.keys(chosen).find((i) => chosen[i] === 'CONFLICT');
  if (bad !== undefined) {
    return { invariant: 'lab3-slot-agreement', message: 'slot ' + bad + ' has conflicting chosen values' };
  }
  return null;
}

export function contiguousExecution(state) {
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    if (node.durable.executedCount !== node.durable.executedThrough + 1) {
      return {
        invariant: 'lab3-contiguous-execution',
        message: id + ' executed out of order',
      };
    }
  }
  return null;
}

export function atMostOncePerReplica(state) {
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    const seen = new Set();
    for (const x of node.durable.appliedIds) {
      if (seen.has(x)) {
        return { invariant: 'lab3-at-most-once', message: id + ' applied ' + x + ' more than once' };
      }
      seen.add(x);
    }
  }
  return null;
}

export function safeGarbageCollection(state) {
  const floors = state.config.servers.map((id) => {
    const node = state.nodes[id];
    return node ? node.durable.executedThrough : -1;
  });
  const floor = Math.min(...floors);
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    const cleared = [];
    node.volatile.log.forEach((slot, i) => {
      if (slot && slot.status === 'cleared') cleared.push(i);
    });
    const bad = cleared.find((s) => s > floor);
    if (bad !== undefined) {
      return {
        invariant: 'lab3-gc-safety',
        message: id + ' cleared slot ' + bad + ' before every replica executed it',
      };
    }
  }
  return null;
}

export function clientResultCorrespondence(state) {
  const applied = new Set();
  state.config.servers.forEach((id) => {
    const node = state.nodes[id];
    if (node) node.durable.appliedIds.forEach((x) => applied.add(x));
  });
  for (const c of state.config.clients) {
    const node = state.nodes[c];
    if (!node) continue;
    const orphan = node.durable.completed.find((r) => !applied.has(c + '#' + r.seq));
    if (orphan) {
      return {
        invariant: 'lab3-result-correspondence',
        message: 'client ' + c + ' completed seq ' + orphan.seq + ' no replica applied',
      };
    }
  }
  return null;
}

export function checkLab3Invariants(state) {
  return [
    slotAgreement,
    contiguousExecution,
    atMostOncePerReplica,
    safeGarbageCollection,
    clientResultCorrespondence,
  ]
    .map((fn) => fn(state))
    .filter(Boolean);
}
